import { processDynamicHTML } from 'ia-util-helpers'

const productInfo = new Map()

// Quick Buy Opener Component - handles fetch and insertion
export class IaQuickBuyOpener extends HTMLElement {
  connectedCallback() {
    // Set role and tabindex for accessibility
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'button')
    }
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0')
    }

    this.handleClick = this.handleClick.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    
    this.addEventListener('click', this.handleClick)
    this.addEventListener('keydown', this.handleKeyDown)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown(e) {
    // Allow activation with Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.handleClick(e)
    }
  }

  buildRequestUrl(productHandle) {
    return `/products/${productHandle}`
  }

  async fetchProductSection(productHandle) {
    const requestUrl = this.buildRequestUrl(productHandle)

    if (productInfo.has(requestUrl)) {
      return productInfo.get(requestUrl)
    }

    const fetchPromise = fetch(requestUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch product section: ${response.status}`)
        }
        return response.text()
      })
      .then((responseText) => new DOMParser().parseFromString(responseText, 'text/html'))

    productInfo.set(requestUrl, fetchPromise)
    return fetchPromise
  }

  async handleClick(e) {
    e.preventDefault()
    e.stopPropagation()
    
    const productHandle = this.getAttribute('product-handle')
    if (!productHandle) {
      console.warn('ia-quick-buy-opener: product-handle attribute is required')
      return
    }

    try {
      const html = await this.fetchProductSection(productHandle)
      
      // Find the main product section in the fetched HTML
      const productSection = html.querySelector('ia-main-product')
      if (!productSection) {
        console.warn('ia-quick-buy-opener: Product section not found in fetched HTML')
        return
      }

      // Find the section wrapper (shopify-section element)
      const sectionWrapper = productSection.closest('[id^="shopify-section-"]')
      if (!sectionWrapper) {
        console.warn('ia-quick-buy-opener: Section wrapper not found in fetched HTML')
        return
      }

      // Get container
      const container = document.getElementById('ia-quick-buy')
      if (!container) {
        console.error('ia-quick-buy-opener: #ia-quick-buy container not found in page')
        return
      }

      // Create a temporary document with only the section HTML
      const sectionDoc = new DOMParser().parseFromString(sectionWrapper.outerHTML, 'text/html')

      // Process styles and scripts from the section HTML only
      processDynamicHTML(sectionDoc)
      
      // Always create fresh quick-buy wrapper element
      const quickBuyElement = document.createElement('ia-quick-buy')
      const clonedSection = productSection.cloneNode(true)
      quickBuyElement.appendChild(clonedSection)

      // Insert wrapper into container
      container.innerHTML = ''
      container.appendChild(quickBuyElement)

      // Trigger custom event for any components that need to initialize
      document.dispatchEvent(new CustomEvent('shopify:section:load'))
    } catch (error) {
      console.error('ia-quick-buy-opener: Error fetching product section:', error)
    }
  }
}

// Quick Buy Wrapper Component - empty for now, just a wrapper for future JS
export class IaQuickBuy extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('ia-drawer-component')
    const title = this.querySelector('.ia-block-title')
    if (this.drawer) {
      this.drawer.open()
    }
    const infoLink = this.querySelector('[data-quick-buy-info-link]')
    if (infoLink) {
      infoLink.classList.remove('ia-hidden')
    }
    if (title && title.textContent.trim() !== '') {
      title.style.display = 'none'
      this.drawer.querySelector('.ia-drawer__title').textContent = title.textContent
    }
  }
}

customElements.define('ia-quick-buy-opener', IaQuickBuyOpener)
customElements.define('ia-quick-buy', IaQuickBuy)
